import React, { useState, useMemo } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Activity, 
  Clock, 
  Target,
  Brain,
  Eye,
  CheckCircle2,
  Download,
  Play,
  Pause,
} from 'lucide-react';
import { Card } from '../../ui/Card'
import { Badge } from '../../ui/Badge'
import { Button } from '../../ui/Button'
import { ThreatActivityChart } from '../../charts/ThreatActivityChart'
import { useThreatDetectionMetrics, useThreatEvents, useAnomalyDetections } from '../../../hooks/useSecurity'
import type { ThreatSeverity } from '../../../types/security'
import { cn } from '../../../utils/cn'
import { format } from '../../../utils/format'

export const ThreatDetectionDashboard: React.FC = () => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('1h');
  const [severityFilter] = useState<ThreatSeverity[]>([]);
  const [realTimeEnabled, setRealTimeEnabled] = useState(true);
  const [selectedThreat, setSelectedThreat] = useState<string | null>(null);

  const { data: metrics, isLoading: metricsLoading } = useThreatDetectionMetrics(
    realTimeEnabled ? 15000 : 60000, // 15s for real-time, 60s otherwise
  );
  
  // Create stable time range with ISO string dates to prevent re-renders
  const timeRange = useMemo(() => {
    const range = getTimeRange(selectedTimeRange);
    return {
      start: range.start.toISOString(),
      end: range.end.toISOString(),
    };
  }, [selectedTimeRange]);
  
  const threatFilters = useMemo(() => ({
    severity: severityFilter.length > 0 ? severityFilter : undefined,
    limit: 50,
    timeRange,
  }), [severityFilter, timeRange]);

  const { data: threats, isLoading: threatsLoading } = useThreatEvents(threatFilters);

  const anomalyFilters = useMemo(() => ({
    severity: severityFilter.length > 0 ? severityFilter : undefined,
    limit: 20,
    timeRange,
  }), [severityFilter, timeRange]);

  const { data: anomalies } = useAnomalyDetections(anomalyFilters);

  const criticalThreats = useMemo(() => {
    const critical = threats?.filter(t => t.severity === 'P1') || [];
    return critical;
  }, [threats]);

  const activeThreatsByType = useMemo(() => {
    if (!threats) {
return {};
}
    return threats
      .filter(t => t.status !== 'resolved' && t.status !== 'false_positive')
      .reduce((acc, threat) => {
        acc[threat.type] = (acc[threat.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
  }, [threats]);

  const severityStats = useMemo(() => {
    if (!threats) {
      return { P1: 0, P2: 0, P3: 0, P4: 0 };
    }
    const stats = threats.reduce((acc: Record<string, number>, threat) => {
      acc[threat.severity] = (acc[threat.severity] || 0) + 1;
      return acc;
    }, { P1: 0, P2: 0, P3: 0, P4: 0 } as Record<string, number>);
    return stats;
  }, [threats]);

  const handleExportReport = () => {
    // Export threat detection report
    console.log('Exporting threat detection report...');
  };

  if (metricsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-danger-600 rounded-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-console-text">Threat Detection Center</h1>
            <p className="text-console-muted">AI-powered threat detection and response</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Button
            variant={realTimeEnabled ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setRealTimeEnabled(!realTimeEnabled)}
          >
            {realTimeEnabled ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {realTimeEnabled ? 'Pause' : 'Resume'} Real-time
          </Button>
          
          <Button variant="secondary" size="sm" onClick={handleExportReport}>
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Real-time Status */}
      <div className="flex items-center gap-2 p-3 bg-console-surface/50 rounded-lg border border-console-border">
        <div className={cn(
          'w-2 h-2 rounded-full',
          realTimeEnabled ? 'bg-status-online animate-pulse' : 'bg-console-muted',
        )} />
        <span className="text-sm text-console-muted">
          {realTimeEnabled ? 'Real-time monitoring active' : 'Real-time monitoring paused'}
        </span>
        {metrics && (
          <span className="text-sm text-console-text ml-auto">
            Last updated: {format.relativeTime(new Date())}
          </span>
        )}
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Active Threats</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.activeThreats || 0}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {metrics?.overview.criticalThreats || 0} critical
              </p>
            </div>
            <div className="p-3 bg-danger-600/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Mitigation Rate</p>
              <p className="text-2xl font-bold text-console-text">
                {metrics?.overview.mitigationRate?.toFixed(1) || '0'}%
              </p>
              <p className="text-xs text-status-online mt-1">
                {metrics?.overview.falsePositiveRate?.toFixed(1) || '0'}% false positive
              </p>
            </div>
            <div className="p-3 bg-primary-600/20 rounded-lg">
              <Target className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">Avg Detection Time</p>
              <p className="text-2xl font-bold text-console-text">
                {format.duration(metrics?.overview.averageDetectionTime || 0)}
              </p>
              <p className="text-xs text-console-muted mt-1">
                {format.duration(metrics?.overview.averageResolutionTime || 0)} to resolve
              </p>
            </div>
            <div className="p-3 bg-warning-600/20 rounded-lg">
              <Clock className="w-6 h-6 text-warning-600" />
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-console-muted text-sm font-medium">ML Models Active</p>
              <p className="text-2xl font-bold text-console-text">5</p>
              <p className="text-xs text-status-online mt-1">
                98.7% accuracy
              </p>
            </div>
            <div className="p-3 bg-secondary-600/20 rounded-lg">
              <Brain className="w-6 h-6 text-secondary-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Threat Severity Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-console-text">Real-time Threat Activity</h3>
            <div className="flex items-center gap-2">
              <select
                value={selectedTimeRange}
                onChange={(e) => setSelectedTimeRange(e.target.value)}
                className="bg-console-surface border border-console-border rounded px-3 py-1 text-sm text-console-text"
              >
                <option value="15m">15 minutes</option>
                <option value="1h">1 hour</option>
                <option value="6h">6 hours</option>
                <option value="24h">24 hours</option>
              </select>
            </div>
          </div>
          
          {/* Real-time threat activity chart */}
          {metrics?.realTime && metrics.realTime.length > 0 ? (
            <ThreatActivityChart
              data={metrics.realTime.map((point) => ({
                timestamp: point.timestamp ? new Date(point.timestamp) : new Date(),
                anomalyScore: point.anomalyScore ?? 0,
                networkActivity: point.networkActivity ?? 0,
                suspiciousConnections: point.suspiciousConnections ?? 0,
                authenticationFailures: point.authenticationFailures ?? 0,
                unauthorizedAccess: point.unauthorizedAccess ?? 0,
              }))}
              height={256}
            />
          ) : (
            <div className="h-64 bg-console-surface/30 rounded-lg border border-console-border flex items-center justify-center">
              <div className="text-center">
                <Activity className="w-8 h-8 text-console-muted mx-auto mb-2" />
                <p className="text-console-muted">No threat activity data available</p>
                <p className="text-xs text-console-muted mt-1">
                  Threat monitoring will begin when events are detected
                </p>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold text-console-text mb-4">Threat Severity</h3>
          {/* Debug info */}
          <div className="text-xs text-console-muted mb-2">
            threats: {threats ? `${threats.length} items` : 'null'}, 
            loading: {threatsLoading ? 'yes' : 'no'},
            criticalThreats: {criticalThreats.length},
            severityStats: {JSON.stringify(severityStats)}
          </div>
          <div className="space-y-4">
            {Object.entries(severityStats).map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={getSeverityVariant(severity as ThreatSeverity)}
                    size="sm"
                  >
                    {severity}
                  </Badge>
                  <span className="text-sm text-console-text">{getSeverityLabel(severity as ThreatSeverity)}</span>
                </div>
                <span className="text-sm font-medium text-console-text">{count}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-6 pt-4 border-t border-console-border">
            <div className="flex items-center justify-between text-sm">
              <span className="text-console-muted">Total Active</span>
              <span className="font-medium text-console-text">
                {Object.values(severityStats).reduce((a, b) => a + b, 0)}
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Active Threats and Anomalies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-console-text">Critical Threats</h3>
            <Badge variant="danger" size="sm">
              {criticalThreats.length} active
            </Badge>
          </div>
          
          <div className="space-y-3">
            {criticalThreats.slice(0, 5).map((threat) => (
              <div
                key={threat.id}
                className={cn(
                  'p-3 rounded-lg border cursor-pointer transition-colors',
                  selectedThreat === threat.id
                    ? 'border-primary-600 bg-primary-600/10'
                    : 'border-console-border bg-console-surface/30 hover:bg-console-surface/50',
                )}
                role="button"
                tabIndex={0}
                aria-pressed={selectedThreat === threat.id}
                onClick={() => setSelectedThreat(threat.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedThreat(threat.id);
                  }
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant={getSeverityVariant(threat.severity as ThreatSeverity)} size="sm">
                    {threat.severity}
                  </Badge>
                  <span className="text-xs text-console-muted">
                    {format.relativeTime(threat.timestamp)}
                  </span>
                </div>
                
                <h4 className="font-medium text-console-text text-sm mb-1">
                  {threat.type.replace('_', ' ').toUpperCase()}
                </h4>
                
                <p className="text-xs text-console-muted mb-2">
                  {threat.description}
                </p>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-console-muted">
                    Node: {threat.nodeId}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-console-muted">Risk:</span>
                    <span className={cn(
                      'font-medium',
                      (threat.riskScore ?? 0) > 80 ? 'text-danger-600' :
                      (threat.riskScore ?? 0) > 60 ? 'text-warning-600' :
                      'text-status-online',
                    )}>
                      {threat.riskScore}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {criticalThreats.length === 0 && (
            <div className="text-center py-8">
              <CheckCircle2 className="w-8 h-8 text-status-online mx-auto mb-2" />
              <p className="text-console-muted">No critical threats detected</p>
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-console-text">ML Anomaly Detection</h3>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-secondary-600 rounded-full animate-pulse" />
              <span className="text-xs text-console-muted">Learning</span>
            </div>
          </div>
          
          <div className="space-y-3">
            {anomalies?.slice(0, 5).map((anomaly) => (
              <div
                key={anomaly.id}
                className="p-3 rounded-lg border border-console-border bg-console-surface/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Brain className="w-4 h-4 text-secondary-600" />
                    <Badge variant={getSeverityVariant(anomaly.severity as ThreatSeverity)} size="sm">
                      {anomaly.severity}
                    </Badge>
                  </div>
                  <span className="text-xs text-console-muted">
                    {format.relativeTime(anomaly.timestamp)}
                  </span>
                </div>
                
                <h4 className="font-medium text-console-text text-sm mb-1">
                  {anomaly.category}
                </h4>
                
                <p className="text-xs text-console-muted mb-2">
                  {anomaly.description}
                </p>
                
                <div className="flex items-center justify-between text-xs">
                  <span className="text-console-muted">
                    Model: {anomaly.modelId}
                  </span>
                  <div className="flex items-center gap-1">
                    <span className="text-console-muted">Score:</span>
                    <span className={cn(
                      'font-medium',
                      (anomaly.score ?? 0) > 0.8 ? 'text-danger-600' :
                      (anomaly.score ?? 0) > 0.6 ? 'text-warning-600' :
                      'text-status-online',
                    )}>
                      {((anomaly.score ?? 0) * 100).toFixed(1)}
                    </span>
                  </div>
                </div>
              </div>
            )) || []}
          </div>
          
          {(!anomalies || anomalies.length === 0) && (
            <div className="text-center py-8">
              <Eye className="w-8 h-8 text-console-muted mx-auto mb-2" />
              <p className="text-console-muted">No anomalies detected</p>
              <p className="text-xs text-console-muted mt-1">ML models are monitoring</p>
            </div>
          )}
        </Card>
      </div>

      {/* Threat Types Distribution */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-console-text mb-4">Active Threats by Type</h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(activeThreatsByType).map(([type, count]) => (
            <div
              key={type}
              className="p-3 rounded-lg border border-console-border bg-console-surface/30 text-center"
            >
              <div className="text-2xl font-bold text-console-text mb-1">{count}</div>
              <div className="text-xs text-console-muted">{type.replace('_', ' ')}</div>
            </div>
          ))}
        </div>
        
        {Object.keys(activeThreatsByType).length === 0 && (
          <div className="text-center py-8">
            <Shield className="w-8 h-8 text-status-online mx-auto mb-2" />
            <p className="text-console-muted">No active threats by type</p>
          </div>
        )}
      </Card>
    </div>
  );
};

// Helper functions
function getTimeRange(range: string): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  
  switch (range) {
    case '15m':
      start.setMinutes(start.getMinutes() - 15);
      break;
    case '1h':
      start.setHours(start.getHours() - 1);
      break;
    case '6h':
      start.setHours(start.getHours() - 6);
      break;
    case '24h':
      start.setHours(start.getHours() - 24);
      break;
    default:
      start.setHours(start.getHours() - 1);
  }
  
  return { start, end };
}

function getSeverityVariant(severity: ThreatSeverity): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' {
  switch (severity) {
    case 'P1': return 'danger';
    case 'P2': return 'warning';
    case 'P3': return 'secondary';
    case 'P4': return 'success';
    default: return 'secondary';
  }
}

function getSeverityLabel(severity: ThreatSeverity): string {
  switch (severity) {
    case 'P1': return 'Critical';
    case 'P2': return 'High';
    case 'P3': return 'Medium';
    case 'P4': return 'Low';
    default: return 'Unknown';
  }
}